/** Who wrote a comment message: persist user id, resolve label on view. */

import { getCloudSession } from "../lib/cloudAuth";
import type { CommentMessage } from "./commentStore";

export type CommentAuthor = {
  email: string;
  userId?: string | undefined;
};

export type DisplayAuthorContext = {
  currentUserId?: string | null;
  currentEmail?: string | null;
  /** Resolved labels keyed by user id (email today; display name via #113). */
  labelsByUserId?: Record<string, string>;
};

let activeAuthor: CommentAuthor | null = null;

/** Set by the editor shell from the cloud session (or cleared when signed out). */
export function setCommentAuthor(author: CommentAuthor | null): void {
  const email = author?.email?.trim() ?? "";
  const userId = author?.userId?.trim() ?? "";
  if (email.length === 0 && userId.length === 0) {
    activeAuthor = null;
    return;
  }
  activeAuthor = { email, ...(userId.length > 0 ? { userId } : {}) };
}

export function getCommentAuthor(): CommentAuthor | null {
  return activeAuthor ?? authorFromCloudSession();
}

function authorFromCloudSession(): CommentAuthor | null {
  try {
    const session = getCloudSession();
    const email = session?.email?.trim() ?? "";
    const userId = session?.userId?.trim() ?? "";
    if (email.length === 0 && userId.length === 0) return null;
    return { email, ...(userId.length > 0 ? { userId } : {}) };
  } catch {
    return null;
  }
}

/** Fields stored on each new message in `<!-- zw-comments -->`. */
export function authorFieldsForWrite(): Pick<
  CommentMessage,
  "authorName" | "authorId"
> {
  const author = getCommentAuthor();
  if (typeof author?.userId === "string" && author.userId.length > 0) {
    return { authorId: author.userId };
  }
  // Offline / unsigned: keep the legacy local label in the store.
  return { authorName: "You" };
}

/**
 * Label for the comment margin. Own comments (or legacy `"You"`) show as
 * "You"; everyone else shows a resolved label, else a legacy stored email.
 */
export function displayCommentAuthor(
  message: Pick<CommentMessage, "authorName" | "authorId"> | null | undefined,
  ctx: DisplayAuthorContext | string | null | undefined = {},
): string {
  // Back-compat: older call sites passed the viewer email as the 2nd arg.
  const context: DisplayAuthorContext =
    typeof ctx === "string" || ctx === null || typeof ctx === "undefined"
      ? { currentEmail: ctx }
      : ctx;

  const authorId = message?.authorId?.trim() ?? "";
  const meId = context.currentUserId?.trim() ?? "";
  if (authorId.length > 0 && meId.length > 0 && authorId === meId) return "You";

  if (authorId.length > 0) {
    const label = context.labelsByUserId?.[authorId]?.trim() ?? "";
    if (label.length > 0) return label;
  }

  const stored = message?.authorName?.trim() ?? "";
  if (stored.length === 0) return "Comment";
  if (stored === "You") return "You";
  const meEmail = context.currentEmail?.trim().toLowerCase() ?? "";
  if (meEmail.length > 0 && stored.toLowerCase() === meEmail) return "You";
  return stored;
}
